import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Linkedin, Mail, MapPin, Heart, ArrowUpRight } from 'lucide-react';
import logoImage from '@/images/meenammalogobg.png';
import Modal from '@/components/ui/modal';

// ── Data ───────────────────────────────────────────────────────────────────────
const QUICK_LINKS = [
  { href: '#about',    label: 'About Us',    dot: 'bg-brand-emerald' },
  { href: '#mission',  label: 'Our Mission', dot: 'bg-brand-indigo'  },
  { href: '#programs', label: 'Programs',    dot: 'bg-brand-amber'   },
  { href: '#impact',   label: 'Support Us',  dot: 'bg-brand-rose'    },
  { href: '#contact',  label: 'Contact Us',  dot: 'bg-brand-emerald' },
];

const SOCIAL_LINKS = [
  { Icon: Facebook,  href: 'https://facebook.com',  label: 'Facebook'  },
  { Icon: Twitter,   href: 'https://twitter.com',   label: 'Twitter'   },
  { Icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
  { Icon: Linkedin,  href: 'https://linkedin.com',  label: 'LinkedIn'  },
];

const MODAL_CONTENT = {
  privacy: {
    title: 'Privacy Policy',
    sections: [
      { heading: 'Information We Collect', body: 'Our trust collects information you provide when reaching out to us, submitting scholarship applications, or contributing to our programs.' },
      { heading: 'How We Use Information', body: 'We utilize your information to handle applications, share program updates, and manage contributions with complete security.' },
      { heading: 'Contact', body: 'For inquiries, reach us at info@meenamma-trust.org' },
    ],
  },
  terms: {
    title: 'Terms of Service',
    sections: [
      { heading: 'About Us', body: 'Our trust delivers comprehensive educational assistance and merit-based scholarships to students throughout Srikakulam, Andhra Pradesh.' },
      { heading: 'Scholarship Terms', body: 'Awards are granted based on academic excellence and economic circumstances. Beneficiaries are expected to uphold educational performance standards.' },
      { heading: 'Contact', body: 'For inquiries, reach us at info@meenamma-trust.org' },
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    sections: [
      { heading: 'What Are Cookies', body: 'Cookies are small data files that enhance your browsing experience on our website.' },
      { heading: 'How We Use Cookies', body: 'We utilize cookies to store your preferences and understand how visitors interact with our site.' },
      { heading: 'Contact', body: 'For inquiries, reach us at info@meenamma-trust.org' },
    ],
  },
};

// ── Variants ────────────────────────────────────────────────────────────────────
const itemVariant = {
  hidden:  { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.09, duration: 0.5 } }),
};

// ── Footer Component ────────────────────────────────────────────────────────────
const Footer = () => {
  const year = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState(null);

  return (
    <footer className="bg-slate-900 text-slate-300 relative overflow-hidden">

      {/* Top gradient accent bar — thicker & more vibrant */}
      <div className="absolute top-0 left-0 right-0 h-[3px] brand-gradient-bg opacity-75" />

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-brand-indigo/5 blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-brand-emerald/5 blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                        w-[500px] h-[500px] rounded-full bg-brand-indigo/3 blur-[120px]" />
      </div>

      {/* Pre-footer CTA band */}
      <div className="relative z-10 border-b border-slate-800/80">
        <div className="container mx-auto px-4 sm:px-6 py-10">
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-between gap-6"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <div>
              <p className="text-slate-100 font-bold text-lg mb-1">
                Ready to make a difference?
              </p>
              <p className="text-slate-400 text-sm">
                Support a student's journey — every contribution creates lasting impact.
              </p>
            </div>
            <a
              href="#impact"
              className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl
                         brand-gradient-bg text-white text-sm font-semibold
                         shadow-lg shadow-brand-indigo/25 hover:opacity-90 hover:-translate-y-0.5
                         transition-all duration-200"
            >
              <Heart className="h-4 w-4" />
              Support Us
            </a>
          </motion.div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 sm:px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">

          {/* Brand column */}
          <motion.div
            className="lg:col-span-2"
            custom={0}
            variants={itemVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-5">
              <img
                src={logoImage}
                alt="Meenamma Educational Trust"
                className="h-16 w-auto drop-shadow-md"
              />
              <span className="text-lg font-logo logo-text leading-tight max-w-[160px]">
                Meenamma Educational Trust
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-md">
              Dedicated to empowering students through educational excellence by offering scholarships,
              academic resources, and comprehensive support throughout Srikakulam, Andhra Pradesh.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Heart className="h-4 w-4 text-brand-rose flex-shrink-0" />
              <span>Building brighter tomorrows since 2024</span>
            </div>
          </motion.div>

          {/* Quick links */}
          <motion.div
            custom={1}
            variants={itemVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="font-bold text-slate-100 mb-5 uppercase tracking-[0.12em] text-[11px]">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map(({ href, label, dot }) => (
                <li key={href}>
                  <a
                    href={href}
                    className="flex items-center gap-2.5 text-sm text-slate-400 hover:text-white
                               transition-colors duration-200 group hover-gradient-underline"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${dot} flex-shrink-0
                                     group-hover:scale-150 transition-transform duration-200`} />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact + Social */}
          <motion.div
            custom={2}
            variants={itemVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Contact */}
            <div>
              <h3 className="font-bold text-slate-100 mb-5 uppercase tracking-[0.12em] text-[11px]">
                Contact
              </h3>
              <div className="space-y-3 text-sm">
                <a
                  href="mailto:info@meenamma-trust.org"
                  className="flex items-center gap-2.5 text-slate-400 hover:text-brand-indigo
                             transition-colors duration-200 group"
                >
                  <Mail className="h-4 w-4 text-brand-indigo flex-shrink-0" />
                  <span>info@meenamma-trust.org</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <div className="flex items-start gap-2.5 text-slate-400">
                  <MapPin className="h-4 w-4 mt-0.5 text-brand-emerald flex-shrink-0" />
                  <span className="leading-relaxed">
                    Vijaya Nagar Colony, Srikakulam<br />
                    Andhra Pradesh 532001, India
                  </span>
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <h3 className="font-bold text-slate-100 mb-4 uppercase tracking-[0.12em] text-[11px]">
                Connect
              </h3>
              <div className="flex gap-2.5">
                {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="p-2.5 rounded-xl bg-slate-800 text-slate-400 border border-slate-700/50
                               hover:bg-brand-indigo hover:text-white hover:border-brand-indigo
                               transition-all duration-200 hover:scale-110 hover:-translate-y-0.5"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-slate-700/60 to-transparent mb-8" />

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="flex flex-col sm:flex-row justify-between items-center gap-4"
        >
          <p className="text-sm text-slate-500">
            &copy; {year}{' '}
            <span className="font-logo text-brand-emerald text-base">Meenamma Educational Trust</span>.
            {' '}All Rights Reserved.
          </p>

          <div className="flex items-center gap-5 text-xs text-slate-500">
            {Object.keys(MODAL_CONTENT).map((key, i) => (
              <React.Fragment key={key}>
                {i > 0 && <span className="text-slate-700">•</span>}
                <button
                  onClick={() => setActiveModal(key)}
                  className="hover:text-slate-300 transition-colors duration-200 underline-offset-2
                             hover:underline"
                >
                  {MODAL_CONTENT[key].title}
                </button>
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      {Object.entries(MODAL_CONTENT).map(([key, { title, sections }]) => (
        <Modal
          key={key}
          isOpen={activeModal === key}
          onClose={() => setActiveModal(null)}
          title={title}
        >
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground">Last updated: {year}</p>
            {sections.map(({ heading, body }) => (
              <div key={heading}>
                <h3 className="text-base font-semibold mb-1.5">{heading}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </Modal>
      ))}
    </footer>
  );
};

export default Footer;
